import { useMemo } from "react";
import { useApp, useUi } from "@/app/AppContext";
import type { TranslationKey } from "@/config/translationKeys";
import {
  ALL_LOCATIONS_ID,
  type SystemAnalysis,
  type SystemDomain,
  type SystemLocation,
  type SystemStatus,
} from "@/domain/systemAnalysis";

const ALL_LOCATIONS: SystemLocation = {
  locationId: ALL_LOCATIONS_ID,
  locationName: { no: "Alle lokasjoner", en: "All locations" },
};

export interface CurrentSystemAnalysis {
  analysis: SystemAnalysis | null;
  locations: SystemLocation[];
  location: SystemLocation | null;
  domains: SystemDomain[];
}

/**
 * The selected analysis (default: the newest) and location. The location is
 * matched case-insensitively so a `?location=` value works as an ID.
 */
export function useCurrentSystemAnalysis(): CurrentSystemAnalysis {
  const { data } = useApp();
  const { system } = useUi();
  return useMemo(() => {
    const analyses = data.systemData.analyses;
    const analysis =
      analyses.find((entry) => entry.analysisId === system.analysisId) ?? analyses[0] ?? null;
    const locations = analysis?.locations.length ? analysis.locations : [ALL_LOCATIONS];
    const wanted = system.locationId.toLowerCase();
    const location =
      locations.find((entry) => entry.locationId.toLowerCase() === wanted) ?? locations[0] ?? null;
    const domains = analysis
      ? (analysis.domainsByLocation[location?.locationId ?? ALL_LOCATIONS_ID] ??
        analysis.domainsByLocation[ALL_LOCATIONS_ID] ??
        [])
      : [];
    return { analysis, locations, location, domains };
  }, [data.systemData, system.analysisId, system.locationId]);
}

const STATUS_LABEL_KEYS: Record<SystemStatus, TranslationKey> = {
  zero: "systemZero",
  fulfilled: "systemFulfilled",
  almost_fulfilled: "systemAlmostFulfilled",
  partially_fulfilled: "systemPartiallyFulfilled",
  only_limited: "systemOnlyLimited",
  not_fulfilled: "systemNotFulfilled",
  insufficient_information: "systemInsufficientInformation",
  unknown: "systemUnknown",
};

export function statusLabelKey(status: SystemStatus): TranslationKey {
  return STATUS_LABEL_KEYS[status];
}

/** Picks the Norwegian or English variant of a `xxxNo` / `xxxEn` pair. */
export function pick(language: string, no: string | undefined, en: string | undefined): string {
  return (language === "en" ? en || no : no || en) ?? "";
}
